<script setup lang="ts">
import { computed, ref } from "vue";

import {
  getPropertyConfig,
  getPropertyDisplayTitle,
  getPropertyPartialTask,
  getPropertyValueFromTask,
} from "~/common/properties";
import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import StatusIcon from "~/components/dumb/StatusIcon.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { makeDropdownItem } from "~/constants/dropdown";
import { UNSET_STATUS_LABEL, UNSET_STATUS_PSEUDO_DUID } from "~/constants/status";
import { colorsByTheme } from "~/constants/style";
import { CheckIcon, ChevronRightIcon, NoValueIcon, StatusFieldIcon } from "~/icons";
import {
  ButtonSize,
  ButtonStyle,
  CommandId,
  EditorMode,
  IconSize,
  Placement,
  StatusKind,
  ViewKind,
} from "~/shared/enums";
import type { DropdownMenuItem, PropertyAnyStatus, PropertyValueForKind, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";

const STATUS_KIND_TO_SHORTCUT_MAP = new Map([
  [StatusKind.UNSTARTED, CommandId.CYCLE_UNSTARTED_STATUSES],
  [StatusKind.STARTED, CommandId.CYCLE_STARTED_STATUSES],
  [StatusKind.BLOCKED, CommandId.CYCLE_BLOCKED_STATUSES],
  [StatusKind.FINISHED, CommandId.CYCLE_FINISHED_STATUSES],
  [StatusKind.CANCELED, CommandId.CYCLE_CANCELED_STATUSES],
]);

type Value = PropertyValueForKind<PropertyAnyStatus>;

const props = defineProps<{
  property: PropertyAnyStatus;
  tasks: Task[];
  value?: Value;
  hideUnset?: boolean;
  showText?: boolean;
  editorMode: EditorMode;
  onAfterClose?: (statusDuid?: string) => void;
}>();

const emit = defineEmits<{
  select: [value: Value];
  update: [value: Value];
  reject: [];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const dropdownMenu = ref<InstanceType<typeof DropdownMenu> | null>(null);
const colors = computed(() => colorsByTheme[pageStore.theme]);

const propertyConfig = computed(() => getPropertyConfig(props.property.kind));
const isDefault = computed(() => propertyConfig.value.isDefault);
const isPropertyNameShown = computed(() => props.property.adtl.isNameShown && !pageStore.isMobile);

const taskKinds = computed(() =>
  dataStore.getTaskKindsByDuidsOrdered([...new Set(props.tasks.map((task) => task.kindDuid))])
);
const statusList = computed(() => dataStore.getStatusList(props.property, taskKinds.value));

const isTrashPage = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);

const isChipMode = computed(
  () => props.editorMode === EditorMode.CHIP || props.editorMode === EditorMode.CHIP_RECOMMENDATION
);
const isBaseListMode = computed(() => props.editorMode === EditorMode.LIST);
const isListMiniNonTcmMode = computed(
  () => props.editorMode === EditorMode.LIST_MINI || props.editorMode === EditorMode.LIST_MINI_ROADMAP
);
const isListMiniTcmMode = computed(() => props.editorMode === EditorMode.LIST_MINI_TCM);
const isListMode = computed(() => isBaseListMode.value || isListMiniNonTcmMode.value || isListMiniTcmMode.value);
const isTaskDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const isFormOrAutomationMode = computed(() => isFormMode.value || isAutomationMode.value);
const isTcmMode = computed(() => props.editorMode === EditorMode.CHIP);

const hasTwoStatuses = computed(
  () => statusList.value.length === 2 && !isContextMenuMode.value && !isFormOrAutomationMode.value
);

const getSelectedTaskDuids = () => {
  if (isListMiniNonTcmMode.value || isListMiniTcmMode.value) {
    const selectedDuids =
      appStore
        .getSubtasksListVisualization()
        ?.getSelectedRows()
        .map((row) => row.data?.duid)
        .filter((taskDuid): taskDuid is string => !!taskDuid) ?? [];
    return selectedDuids.length > 0 ? selectedDuids : props.tasks.map((e) => e.duid);
  }
  if (isBaseListMode.value) {
    return [...appStore.selectedTaskDuids];
  }
  return props.tasks.map((task) => task.duid);
};

const selectedTasks = computed(() =>
  dataStore.getTasksByDuidsOrderedIfLoaded(getSelectedTaskDuids(), {
    includeTrashed: false,
    includeDraft: isTcmMode.value,
  })
);

const selectedStatusDuid = computed(() => {
  if (isFormOrAutomationMode.value) {
    return props.value ?? UNSET_STATUS_PSEUDO_DUID;
  }

  const firstTaskStatus = getPropertyValueFromTask(props.property, props.tasks[0]);
  return props.tasks.every((task) => getPropertyValueFromTask(props.property, task) === firstTaskStatus)
    ? firstTaskStatus
    : undefined;
});
const selectedStatus = computed(() => dataStore.getStatusByDuid(selectedStatusDuid.value ?? ""));

const updateStatus = (duid: string) => {
  const taskDuids = getSelectedTaskDuids();
  const tasksToUpdate = dataStore.getTasksByDuidsOrderedIfLoaded(taskDuids, {
    includeTrashed: false,
    includeDraft: isTcmMode.value,
  });
  dataStore.updateTasks(
    tasksToUpdate.map((task) => ({ duid: task.duid, ...getPropertyPartialTask(props.property, task, duid) }))
  );
};

const nextStatus = computed(() => {
  const indexOfCurrentStatus = statusList.value.findIndex((status) => status.duid === selectedStatusDuid.value);
  return statusList.value[(indexOfCurrentStatus + 1) % statusList.value.length];
});

const finishedStatuses = computed(() => dataStore.getStatusesByKinds([StatusKind.FINISHED], props.property));
const defaultFinishedStatus = computed(
  () => finishedStatuses.value.find((status) => status.locked) ?? finishedStatuses.value[0]
);

const showMarkComplete = computed(() => {
  if (
    hasTwoStatuses.value ||
    finishedStatuses.value.length === 0 ||
    defaultFinishedStatus.value.duid === selectedStatusDuid.value
  ) {
    return false;
  }

  const finishedStatusDuids = new Set(finishedStatuses.value.map((status) => status.duid));
  return selectedTasks.value.map((task) => !finishedStatusDuids.has(getPropertyValueFromTask(props.property, task)));
});

const cycleToNextStatus = () => {
  if (!nextStatus.value) {
    return;
  }
  // There is a race condition with grid selection that requires us to wait for it
  // oxlint-disable-next-line dart/no-set-timeout
  setTimeout(() => updateStatus(nextStatus.value.duid), 1);
};

const markFinished = () => {
  if (!defaultFinishedStatus.value) {
    return;
  }

  updateStatus(defaultFinishedStatus.value.duid);
};

const dropdownSections = computed(() => {
  const firstTaskStatus = isFormOrAutomationMode.value
    ? undefined
    : getPropertyValueFromTask(props.property, props.tasks[0]);
  const statusDuidOfSelectedTasks = isFormOrAutomationMode.value
    ? undefined
    : selectedTasks.value.every((task) => getPropertyValueFromTask(props.property, task) === firstTaskStatus)
      ? firstTaskStatus
      : undefined;

  const makeItem = (isSelected: boolean, item: Omit<DropdownMenuItem, "kind" | "disabled" | "selected">) =>
    makeDropdownItem(isSelected, pageStore.isMobile, item);

  const items: DropdownMenuItem[] = [];
  if (isFormOrAutomationMode.value && !props.hideUnset) {
    items.push(
      makeItem(selectedStatusDuid.value === UNSET_STATUS_PSEUDO_DUID, {
        title: UNSET_STATUS_LABEL,
        icon: NoValueIcon,
        iconArgs: { class: "icon-md text-vlt" },
        onClick: () => emit("select", UNSET_STATUS_PSEUDO_DUID),
      })
    );
  }
  items.push(
    ...statusList.value.map((status) => {
      const isSelected = isFormOrAutomationMode.value
        ? status.duid === selectedStatusDuid.value
        : status.duid === statusDuidOfSelectedTasks;
      return makeItem(isSelected, {
        title: status.title,
        icon: StatusIcon,
        iconArgs: { duid: status.duid, class: "icon-md" },
        commandId:
          isFormOrAutomationMode.value || !isDefault.value
            ? undefined
            : status.locked && status.kind === StatusKind.FINISHED
              ? CommandId.CHANGE_STATUS_TO_DEFAULT_FINISHED
              : STATUS_KIND_TO_SHORTCUT_MAP.get(status.kind),
        onClick: () => {
          if (isFormOrAutomationMode.value) {
            emit("select", status.duid);
            return;
          }
          updateStatus(status.duid);
        },
      });
    })
  );
  return [{ title: "Status", items }];
});

const handleAfterClose = () => {
  props.onAfterClose?.(selectedStatusDuid.value);
};

defineExpose({
  open: () => dropdownMenu.value?.open(),
  isOpen: computed(() => dropdownMenu.value?.isOpen),
});
</script>

<template>
  <DropdownMenu
    ref="dropdownMenu"
    :container="isChipMode ? '#dart-task-creation-modal-wrapper' : isFormMode ? '#dart-form-wrapper' : undefined"
    :disabled="hasTwoStatuses"
    :sections="dropdownSections"
    :block="isListMode || isTaskDetailMode || isFormOrAutomationMode"
    :height-block="isListMode || isFormOrAutomationMode"
    :distance="isFormOrAutomationMode ? 2 : undefined"
    :skidding="isFormOrAutomationMode ? -1 : undefined"
    :cover="!isContextMenuMode && !isFormOrAutomationMode"
    :placement="isContextMenuMode ? Placement.RIGHT_TOP : Placement.BOTTOM_LEFT"
    :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }"
    :class="hasTwoStatuses ? 'cursor-pointer' : ''"
    :on-after-close="handleAfterClose"
    :header-title="`Set ${getPropertyDisplayTitle(property).toLowerCase()}`"
    mobile-include-done
    @click="() => (hasTwoStatuses ? cycleToNextStatus() : undefined)">
    <DropdownMenuItemContent
      v-if="isContextMenuMode"
      :icon="StatusFieldIcon"
      :title="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      is-submenu />
    <Tooltip
      v-else
      :disabled="isFormOrAutomationMode"
      :class="{ 'overflow-hidden': isFormOrAutomationMode }"
      :command-id="isDefault && !hasTwoStatuses ? CommandId.CHANGE_STATUS : undefined"
      :text="
        hasTwoStatuses
          ? `Change ${getPropertyDisplayTitle(property).toLowerCase()} to ${nextStatus.title}`
          : isDefault
            ? undefined
            : `Change ${getPropertyDisplayTitle(property).toLowerCase()}`
      "
      :block="isListMode || isTaskDetailMode || isFormOrAutomationMode"
      :height-block="isListMode || isFormOrAutomationMode">
      <div
        v-if="isTaskDetailMode"
        class="flex h-fit w-full items-center justify-start gap-2 rounded-sm py-1 pr-2 pl-1.5 text-left hover:bg-lt">
        <div v-if="!isListMode && !selectedStatus" class="text-vlt">
          {{ `Set ${getPropertyDisplayTitle(property).toLowerCase()}` }}
        </div>
        <template v-else>
          <StatusIcon
            :duid="selectedStatus?.duid ?? ''"
            :color-hex="selectedStatus === undefined ? colors.textVlt : undefined"
            class="icon-md" />
          <span :title="selectedStatus?.title" class="flex-1 truncate select-none">
            {{ selectedStatus?.title }}
          </span>
          <!-- Quick actions-->
          <div v-if="!pageStore.isPublicView && !isTrashPage" class="ml-1 flex items-center gap-1">
            <Tooltip
              v-if="showMarkComplete"
              :placement="Placement.TOP"
              :distance="10"
              :command-id="CommandId.CHANGE_STATUS_TO_DEFAULT_FINISHED">
              <Button
                :btn-style="ButtonStyle.SECONDARY"
                :size="ButtonSize.SMALL"
                is-contrast
                :icon="CheckIcon"
                :icon-size="IconSize.S"
                a11y-label="Finish task"
                class="!p-0.5 print:hidden"
                data-global-popper-ignore
                @click.stop="markFinished" />
            </Tooltip>
            <Tooltip
              v-if="!hasTwoStatuses && nextStatus.duid !== selectedStatusDuid"
              :text="`Change to next ${getPropertyDisplayTitle(property).toLowerCase()} (${nextStatus.title})`"
              :distance="10"
              :placement="Placement.TOP">
              <Button
                :btn-style="ButtonStyle.SECONDARY"
                :size="ButtonSize.SMALL"
                is-contrast
                :icon="ChevronRightIcon"
                :icon-size="IconSize.S"
                a11y-label="Change to next status"
                class="!p-0.5 print:hidden"
                data-global-popper-ignore
                @click.stop="cycleToNextStatus" />
            </Tooltip>
          </div>
        </template>
      </div>
      <Button
        v-else
        :btn-style="ButtonStyle.CHIP"
        :text="
          showText || (isPropertyNameShown && isListMode)
            ? selectedStatus
              ? selectedStatus.title
              : isFormOrAutomationMode
                ? isAutomationMode
                  ? UNSET_STATUS_LABEL
                  : 'Set status'
                : getPropertyDisplayTitle(property)
            : ''
        "
        :icon="
          isFormOrAutomationMode && !selectedStatus
            ? undefined
            : isDefault || selectedStatus
              ? StatusIcon
              : StatusFieldIcon
        "
        block
        :icon-args="{
          duid: selectedStatus?.duid ?? '',
          colorHex: selectedStatus === undefined && !showText ? colors.textVlt : undefined,
        }"
        :disable-hover="isListMode || isFormOrAutomationMode"
        :borderless="!isChipMode"
        :text-style="isFormOrAutomationMode && !selectedStatus ? 'text-vlt' : undefined"
        :class="{
          '!rounded': !isListMode,
          'gap-2': !isChipMode,
          'pr-3': isListMiniNonTcmMode,
          'pr-5': isListMiniTcmMode,
          'px-1 py-0.5': !isListMode && !isFormOrAutomationMode,
          '!justify-start px-[7px] pr-7': isFormOrAutomationMode,
          'max-w-xs truncate': isChipMode,
          '!justify-start truncate': isListMode && isPropertyNameShown,
        }"
        a11y-label="Status" />
    </Tooltip>
  </DropdownMenu>
</template>
